// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { motion } from 'framer-motion';
import ShimmerButton from './ShimmerButton';
import {
    analytics,
    trackErrorWithContext,
    getSessionContext,
} from '../utils/analytics';
import { AnalyticsEvents } from '../utils/analytics';

interface QRScannerProps {
    onScanSuccess: (deviceId: string, deviceCode: string) => void;
    disabled?: boolean;
}

export default function QRScanner({
    onScanSuccess,
    disabled = false,
}: QRScannerProps) {
    const [error, setError] = useState<string | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const scannerRef = useRef<HTMLDivElement>(null);

    // Scroll to scanner when it opens
    useEffect(() => {
        if (isScanning && scannerRef.current) {
            // Small delay to ensure the scanner is rendered
            setTimeout(() => {
                scannerRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }, 100);
        }
    }, [isScanning]);

    const handleScan = async (result: any) => {
        if (result && result.length > 0) {
            const scanStartTime = Date.now();

            try {
                // The QR code should contain a URL with deviceId and deviceCode parameters
                const scannedText = result[0].rawValue;
                console.log(
                    '[PAIR-APP] QR Scanner: Scanned text:',
                    scannedText
                );

                // Track (non-blocking) QR code scan attempt
                analytics.capture(AnalyticsEvents.QR_CODE_SCANNED, {
                    method: 'camera',
                    scanned_text_length: scannedText.length,
                    is_url: scannedText.startsWith('http'),
                    scan_time: Date.now() - scanStartTime,
                    ...getSessionContext(),
                });

                // Parse the URL to extract deviceId and deviceCode/shortSecret
                const url = new URL(scannedText);
                const deviceId = url.searchParams.get('deviceId');
                const deviceCode = url.searchParams.get('deviceCode');
                const shortSecret = url.searchParams.get('shortSecret');

                // Use deviceCode if available, otherwise use shortSecret
                const code = deviceCode || shortSecret;

                if (deviceId && code) {
                    console.log('[PAIR-APP] QR Scanner: Found device params:', {
                        deviceId,
                        code,
                        isShortSecret: !deviceCode && !!shortSecret,
                    });

                    // Track (non-blocking) successful QR scan
                    analytics.capture(AnalyticsEvents.QR_CODE_SCANNED, {
                        method: 'camera',
                        success: true,
                        device_id: deviceId,
                        has_device_code: !!deviceCode,
                        has_short_secret: !!shortSecret,
                        used_short_secret: !deviceCode && !!shortSecret,
                        total_scan_time: Date.now() - scanStartTime,
                        ...getSessionContext(),
                    });

                    onScanSuccess(deviceId, code);
                } else {
                    const errorMsg =
                        'Invalid QR code. Please scan a valid Niio player QR code.';
                    setError(errorMsg);

                    // Track (non-blocking) invalid QR code
                    analytics.capture(AnalyticsEvents.PAIRING_FAILED, {
                        failure_type: 'invalid_qr_code',
                        error_message: errorMsg,
                        scanned_text_preview: scannedText.substring(0, 100),
                        has_device_id: !!deviceId,
                        has_device_code: !!deviceCode,
                        has_short_secret: !!shortSecret,
                        scan_time: Date.now() - scanStartTime,
                        ...getSessionContext(),
                    });
                }
            } catch (err) {
                console.error(
                    '[PAIR-APP] QR Scanner: Error parsing QR code:',
                    err
                );
                const errorMsg = 'Invalid QR code format. Please try again.';
                setError(errorMsg);

                // Track (non-blocking) QR parsing error
                await trackErrorWithContext(
                    err instanceof Error ? err : new Error(String(err)),
                    {
                        step: 'qr_scan_parsing',
                        error_type: 'qr_parse_error',
                        scan_time: Date.now() - scanStartTime,
                    }
                );

                analytics.capture(AnalyticsEvents.PAIRING_FAILED, {
                    failure_type: 'qr_parse_error',
                    error_message: errorMsg,
                    error_details:
                        err instanceof Error ? err.message : String(err),
                    scan_time: Date.now() - scanStartTime,
                    ...getSessionContext(),
                });
            }
        }
    };

    const handleError = async (error: any) => {
        console.error('[PAIR-APP] QR Scanner: Error:', error);
        const errorMsg = 'Failed to access camera. Please check permissions.';
        setError(errorMsg);

        // Track (non-blocking) camera access error
        await trackErrorWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                step: 'qr_scanner_camera',
                error_type: 'camera_access_error',
            }
        );

        analytics.capture(AnalyticsEvents.PAIRING_FAILED, {
            failure_type: 'camera_access_error',
            error_message: errorMsg,
            error_details:
                error instanceof Error ? error.message : String(error),
            ...getSessionContext(),
        });
    };

    const handleStartScanning = async () => {
        setIsScanning(true);
        setError(null);

        // Track (non-blocking) QR scanner opened
        analytics.capture(AnalyticsEvents.QR_SCANNER_OPENED, {
            method: 'qr_scanner',
            step: 'scanner_opened',
            ...getSessionContext(),
        });

        // Also track as pairing started
        analytics.capture(AnalyticsEvents.PAIRING_STARTED, {
            method: 'qr_scanner',
            step: 'scanner_opened',
            ...getSessionContext(),
        });
    };

    const handleCancelScanning = async () => {
        setIsScanning(false);

        // Track (non-blocking) QR scanner cancelled (not a step completion, just a user action)
        analytics.capture('qr_scanner_cancelled', {
            action: 'user_cancelled',
            ...getSessionContext(),
        });
    };

    return (
        <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
        >
            <div className="text-center mb-4">
                <h2 className="text-2xl font-bold text-white mb-2">
                    Scan Niio Player QR Code
                </h2>
                <p className="text-gray-400">Point camera at QR code</p>
            </div>

            {disabled ? (
                <div className="flex justify-center">
                    <ShimmerButton disabled={true} className="w-full max-w-xs">
                        <span className="flex items-center justify-center gap-2">
                            <svg
                                className="animate-spin h-5 w-5"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                />
                            </svg>
                            Pairing...
                        </span>
                    </ShimmerButton>
                </div>
            ) : !isScanning ? (
                <div className="flex justify-center">
                    <ShimmerButton
                        onClick={handleStartScanning}
                        className="w-full max-w-xs"
                    >
                        <span className="flex items-center justify-center gap-2">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                                />
                            </svg>
                            Scan QR Code
                        </span>
                    </ShimmerButton>
                </div>
            ) : (
                <div className="relative" ref={scannerRef}>
                    <div className="rounded-lg overflow-hidden bg-black">
                        <Scanner
                            onScan={handleScan}
                            onError={handleError}
                            constraints={{
                                facingMode: 'environment',
                            }}
                            formats={['qr_code']}
                            components={{
                                audio: false,
                                finder: true,
                            }}
                            styles={{
                                container: {
                                    width: '100%',
                                    height: '400px',
                                },
                            }}
                        />
                    </div>
                    <button
                        onClick={handleCancelScanning}
                        className="mt-4 w-full text-gray-400 hover:text-gray-300 text-sm underline"
                    >
                        Cancel scanning
                    </button>
                </div>
            )}

            {error && (
                <motion.div
                    className="text-center p-4 bg-red-900/20 border border-red-700/50 rounded-lg"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                >
                    <p className="text-red-400 text-sm">❌ {error}</p>
                </motion.div>
            )}
        </motion.div>
    );
}
